import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { searchPublicMoments } from '../services/momentSearch.service';
import { deriveMomentSearchQuickSearches, type MomentSearchQuickSearch } from '../utils/momentSearchQuickSearches';

const PREVIEW_CARDS = [
  {
    kicker: 'Live now',
    title: 'Jump into what matters',
    body: 'Find public live sessions and open the web player without installing the app first.',
  },
  {
    kicker: 'Upcoming',
    title: 'Know what to follow',
    body: 'Discover upcoming streams and move to the app when you want spoiler-free notifications.',
  },
  {
    kicker: 'Past moments',
    title: 'Catch what you missed',
    body: 'Browse fan-visible moments from ended sessions and watch directly on the web.',
  },
];

export const MomentSearchSection: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [quickSearches, setQuickSearches] = useState<MomentSearchQuickSearch[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loadQuickSearches = async () => {
      try {
        const response = await searchPublicMoments({ limit: 4 });
        if (!cancelled) {
          setQuickSearches(deriveMomentSearchQuickSearches(response));
        }
      } catch {
        if (!cancelled) {
          setQuickSearches([]);
        }
      }
    };

    loadQuickSearches();

    return () => {
      cancelled = true;
    };
  }, []);

  const submitSearch = (nextQuery: string, mode: 'search' | 'ticker' = 'search') => {
    const trimmed = nextQuery.trim();
    const params = new URLSearchParams();
    if (trimmed) {
      params.set('q', trimmed);
    }
    if (mode === 'ticker') {
      params.set('mode', 'ticker');
    }
    const search = params.toString();
    navigate(`/search${search ? `?${search}` : ''}`);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitSearch(query);
  };

  return (
    <section className="moment-search-entry" aria-labelledby="moment-search-entry-title">
      <div className="moment-search-entry-background" aria-hidden="true" />
      <div className="moment-search-entry-shell">
        <h2 id="moment-search-entry-title">
          <span className="gradient-text">
            {intl.formatMessage({
              id: 'landing.momentSearch.title',
              defaultMessage: 'Find Moments',
            })}
          </span>
        </h2>
        <p className="moment-search-entry-subtitle">
          {intl.formatMessage({
            id: 'landing.momentSearch.subtitleLine1',
            defaultMessage: 'Search live events, upcoming streams, publishers, categories, and past moments.',
          })}
          <br />
          {intl.formatMessage({
            id: 'landing.momentSearch.subtitleLine2',
            defaultMessage: 'Watch on web first, then open the app when you want to follow or get notifications.',
          })}
        </p>

        <section className="moment-search-entry-card" aria-label="Moment search">
          <form className="moment-search-entry-row" onSubmit={handleSubmit}>
          <label className="moment-search-entry-label" htmlFor="landing-moment-search">
            {intl.formatMessage({
              id: 'landing.momentSearch.inputLabel',
              defaultMessage: 'Search HypeDirector moments',
            })}
          </label>
            <span className="moment-search-entry-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="m21 21-4.3-4.3m1.3-5.2a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <input
              id="landing-moment-search"
              className="moment-search-entry-input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={intl.formatMessage({
                id: 'landing.momentSearch.placeholder',
                defaultMessage: 'Search teams, sports, events, creators, publishers...',
              })}
              maxLength={80}
            />
            {query && (
              <button
                className="moment-search-clear-button"
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                title="Clear"
              >
                ×
              </button>
            )}
            <span className="moment-search-entry-actions">
              <button className="moment-search-entry-button" type="submit">
                {intl.formatMessage({
                  id: 'landing.momentSearch.submit',
                  defaultMessage: 'Search',
                })}
              </button>
              <button
                className="moment-search-entry-button moment-search-entry-button-secondary"
                type="button"
                onClick={() => submitSearch(query, 'ticker')}
              >
                {intl.formatMessage({
                  id: 'landing.momentSearch.ticker',
                  defaultMessage: 'Ticker',
                })}
              </button>
            </span>
          </form>
        </section>

        {quickSearches.length > 0 && (
          <div className="moment-search-entry-chips" aria-label="Suggested searches">
            {quickSearches.map((quickSearch) => (
              <button
                key={quickSearch.query}
                type="button"
                onClick={() => submitSearch(quickSearch.query)}
              >
                {quickSearch.label}
              </button>
            ))}
          </div>
        )}

        <section className="moment-search-entry-preview-grid" aria-label="What you can discover">
          {PREVIEW_CARDS.map((card) => (
            <article key={card.kicker} className="moment-search-entry-preview-card">
              <div className="moment-search-entry-preview-kicker">{card.kicker}</div>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </section>
      </div>
    </section>
  );
};
